import { 
  createWithdraw,
} from '@/services/bettingService';

export default {
  namespaced: true,
  
  state: {
    // Bets
    myBets: [],
    betHistory: [],
    activeBets: [],
    
    // Accounts
    accounts: [],
    selectedAccount: null,
    accountBalance: 0,
    
    // Withdrawals
    withdrawals: [],
    pendingWithdrawals: [],
    
    // Statistics
    stats: {
      totalBets: 0,
      wonBets: 0,
      lostBets: 0,
      totalWagered: 0,
      totalWon: 0,
      totalLost: 0,
      netProfit: 0,
      winRate: 0,
      averageBet: 0,
      biggestWin: 0,
      biggestLoss: 0,
    },
    
    // UI State
    selectedPeriod: 'week', // 'day', 'week', 'month', 'year', 'all'
    isLoading: false,
    error: null,
  },
  
  mutations: {
    SET_MY_BETS(state, bets) {
      state.myBets = bets;
      // Separate active and history
      state.activeBets = bets.filter(bet => bet.status === 'pending' || bet.status === 'active');
      state.betHistory = bets.filter(bet => bet.status === 'settled' || bet.status === 'cancelled');
    },
    
    ADD_BET(state, bet) {
      state.myBets.unshift(bet);
      if (bet.status === 'pending' || bet.status === 'active') {
        state.activeBets.unshift(bet);
      }
    },
    
    UPDATE_BET(state, updatedBet) {
      // Update in all arrays
      ['myBets', 'activeBets', 'betHistory'].forEach(arrayName => {
        const index = state[arrayName].findIndex(bet => bet.id === updatedBet.id);
        if (index !== -1) {
          state[arrayName].splice(index, 1, updatedBet);
        }
      });
      
      // Move bet between active and history if status changed
      if (updatedBet.status === 'settled' || updatedBet.status === 'cancelled') {
        state.activeBets = state.activeBets.filter(bet => bet.id !== updatedBet.id);
        if (!state.betHistory.find(bet => bet.id === updatedBet.id)) {
          state.betHistory.unshift(updatedBet);
        }
      }
    },
    
    REMOVE_BET(state, betId) {
      state.myBets = state.myBets.filter(bet => bet.id !== betId);
      state.activeBets = state.activeBets.filter(bet => bet.id !== betId);
      state.betHistory = state.betHistory.filter(bet => bet.id !== betId);
    },
    
    SET_ACCOUNTS(state, accounts) {
      state.accounts = accounts;
    },
    
    SET_SELECTED_ACCOUNT(state, account) {
      state.selectedAccount = account;
      state.accountBalance = account?.balance || 0;
    },
    
    UPDATE_ACCOUNT_BALANCE(state, { accountId, amount }) {
      const account = state.accounts.find(acc => acc.id === accountId);
      if (account) {
        account.balance += amount;
        if (state.selectedAccount?.id === accountId) {
          state.accountBalance = account.balance;
        }
      }
    },
    
    SET_WITHDRAWALS(state, withdrawals) {
      state.withdrawals = withdrawals;
      state.pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
    },
    
    ADD_WITHDRAWAL(state, withdrawal) {
      state.withdrawals.unshift(withdrawal);
      if (withdrawal.status === 'pending') {
        state.pendingWithdrawals.unshift(withdrawal);
      }
    },
    
    REMOVE_WITHDRAWAL(state, withdrawalId) {
      state.withdrawals = state.withdrawals.filter(w => w.id !== withdrawalId);
      state.pendingWithdrawals = state.pendingWithdrawals.filter(w => w.id !== withdrawalId);
    },
    
    SET_STATS(state, stats) {
      state.stats = { ...state.stats, ...stats };
    },
    
    SET_PERIOD(state, period) {
      state.selectedPeriod = period;
    },
    
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
  },
  
  actions: {
    async fetchMyBets({ commit, state, dispatch }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const username = localStorage.getItem('username');
        if (!username) {
          throw new Error('User not logged in');
        }
        
        // const bets = await getUserBets(username);
        // commit('SET_MY_BETS', bets);
        commit('SET_MY_BETS', []); // Temporarily return empty array
        
        // Calculate statistics
        dispatch('calculateStats');
      } catch (error) {
        console.error('Error fetching bets:', error);
        commit('SET_ERROR', error.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async placeBet({ commit, dispatch, rootGetters }, betData) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        // Validate bet amount
        if (betData.amount <= 0) {
          throw new Error('Valor da aposta deve ser maior que zero');
        }
        
        // Check account balance
        const account = state.accounts.find(acc => acc.id === betData.accountId);
        if (!account || account.balance < betData.amount) {
          throw new Error('Saldo insuficiente na conta');
        }
        
        // Create bet
        // const newBet = await createBet(betData);
        // commit('ADD_BET', newBet);
        
        // Temporarily disable bet creation
        throw new Error('createBet function not available');
      } catch (error) {
        console.error('Error placing bet:', error);
        commit('SET_ERROR', error.message);
        dispatch('notifications/showError', error.message, { root: true });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async cancelBet({ commit, dispatch }, betId) {
      try {
        // await deleteBet(betId);
        // commit('REMOVE_BET', betId);
        
        // Temporarily disable bet cancellation
        throw new Error('deleteBet function not available');
      } catch (error) {
        console.error('Error cancelling bet:', error);
        dispatch('notifications/showError', 'Erro ao cancelar aposta', { root: true });
        throw error;
      }
    },
    
    async requestWithdrawal({ commit, dispatch }, withdrawalData) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        // Validate withdrawal
        if (withdrawalData.amount <= 0) {
          throw new Error('Valor do saque deve ser maior que zero');
        }
        
        // Check account balance
        const account = state.accounts.find(acc => acc.id === withdrawalData.accountId);
        if (!account || account.balance < withdrawalData.amount) {
          throw new Error('Saldo insuficiente para saque');
        }
        
        // Create withdrawal
        const withdrawal = await createWithdraw(withdrawalData.accountId, withdrawalData);
        commit('ADD_WITHDRAWAL', withdrawal);
        
        // Update account balance
        commit('UPDATE_ACCOUNT_BALANCE', {
          accountId: withdrawalData.accountId,
          amount: -withdrawalData.amount
        });
        
        dispatch('notifications/showSuccess', 'Saque solicitado com sucesso!', { root: true });
        
        return withdrawal;
      } catch (error) {
        console.error('Error requesting withdrawal:', error);
        commit('SET_ERROR', error.message);
        dispatch('notifications/showError', error.message, { root: true });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchWithdrawals({ commit, state }) {
      try {
        // const withdrawals = await getWithdrawals();
        const withdrawals = []; // Temporarily return empty array
        commit('SET_WITHDRAWALS', withdrawals);
      } catch (error) {
        console.error('Error fetching withdrawals:', error);
        commit('SET_ERROR', error.message);
      }
    },
    
    calculateStats({ commit, state }) {
      const bets = state.betHistory;
      const stats = {
        totalBets: bets.length,
        wonBets: 0,
        lostBets: 0,
        totalWagered: 0,
        totalWon: 0,
        totalLost: 0,
        netProfit: 0,
        winRate: 0,
        averageBet: 0,
        biggestWin: 0,
        biggestLoss: 0,
      };
      
      // Filter by selected period
      const now = new Date();
      const filteredBets = bets.filter(bet => {
        if (state.selectedPeriod === 'all') return true;
        
        const betDate = new Date(bet.settledAt || bet.createdAt);
        const daysDiff = (now - betDate) / (1000 * 60 * 60 * 24);
        
        switch (state.selectedPeriod) {
          case 'day': return daysDiff <= 1;
          case 'week': return daysDiff <= 7;
          case 'month': return daysDiff <= 30;
          case 'year': return daysDiff <= 365;
          default: return true;
        }
      });
      
      // Calculate stats
      filteredBets.forEach(bet => {
        stats.totalWagered += bet.amount;
        
        if (bet.status === 'won') {
          stats.wonBets++;
          stats.totalWon += bet.payout || 0;
          const profit = (bet.payout || 0) - bet.amount;
          if (profit > stats.biggestWin) {
            stats.biggestWin = profit;
          }
        } else if (bet.status === 'lost') {
          stats.lostBets++;
          stats.totalLost += bet.amount;
          if (bet.amount > stats.biggestLoss) {
            stats.biggestLoss = bet.amount;
          }
        }
      });
      
      // Calculate derived stats
      stats.netProfit = stats.totalWon - stats.totalWagered;
      stats.winRate = stats.totalBets > 0 
        ? (stats.wonBets / stats.totalBets * 100).toFixed(2) 
        : 0;
      stats.averageBet = stats.totalBets > 0 
        ? (stats.totalWagered / stats.totalBets).toFixed(2) 
        : 0;
      
      commit('SET_STATS', stats);
    },
    
    setPeriod({ commit, dispatch }, period) {
      commit('SET_PERIOD', period);
      dispatch('calculateStats');
    },
    
    async refreshBettingData({ dispatch }) {
      await Promise.all([
        dispatch('fetchMyBets'),
        dispatch('fetchWithdrawals'),
      ]);
    },
  },
  
  getters: {
    activeBets: state => state.activeBets,
    betHistory: state => state.betHistory,
    
    betsByGame: state => gameId => {
      return state.myBets.filter(bet => bet.gameId === gameId);
    },
    
    betsByAccount: state => accountId => {
      return state.myBets.filter(bet => bet.accountId === accountId);
    },
    
    totalActiveAmount: state => {
      return state.activeBets.reduce((sum, bet) => sum + bet.amount, 0);
    },
    
    accounts: state => state.accounts,
    activeAccounts: state => state.accounts.filter(acc => acc.active),
    selectedAccount: state => state.selectedAccount,
    accountBalance: state => state.accountBalance,
    
    withdrawals: state => state.withdrawals,
    pendingWithdrawals: state => state.pendingWithdrawals,
    
    stats: state => state.stats,
    profitPercentage: state => {
      if (state.stats.totalWagered === 0) return 0;
      return ((state.stats.netProfit / state.stats.totalWagered) * 100).toFixed(2);
    },
    
    recentActivity: state => {
      const allActivity = [
        ...state.myBets.map(bet => ({ ...bet, type: 'bet' })),
        ...state.withdrawals.map(w => ({ ...w, type: 'withdrawal' })),
      ];
      
      return allActivity
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 20);
    },
  },
};